import React, { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Card,
  Row,
  Col,
  Select,
  Input,
  Button,
  Space,
  message,
  Spin,
  Pagination
} from 'antd';
import { ReloadOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';
import { getTasks } from '../../services/taskApi';

const { Option } = Select;

const TaskList = ({
  projectId,
  onTaskSelect,
  onEdit,
  onDelete,
  onStatusChange
}) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchTasks = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = {
        ...filters,
        page: params.page || pagination.page,
        limit: pagination.limit
      };

      const response = await getTasks(projectId, queryParams);

      if (response.success) {
        setTasks(response.data || []);
        setPagination(response.pagination || pagination);
      } else {
        throw new Error(response.message || 'เกิดข้อผิดพลาดในการดึงข้อมูลงาน');
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err.message || 'ไม่สามารถดึงข้อมูลงานได้');
      message.error('เกิดข้อผิดพลาดในการดึงข้อมูลงาน');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
    fetchTasks({ page });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearch = () => {
    fetchTasks();
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      priority: '',
      search: ''
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchTasks();
  };

  useEffect(() => {
    if (projectId) {
      fetchTasks();
    }
  }, [projectId]);

  const getStatusTag = (status) => {
    const map = {
      todo: { color: 'default', text: 'รอดำเนินการ' },
      in_progress: { color: 'blue', text: 'กำลังดำเนินการ' },
      testing: { color: 'orange', text: 'ทดสอบ' },
      done: { color: 'green', text: 'เสร็จสิ้น' }
    };
    const conf = map[status] || { color: 'default', text: status };
    return <Tag color={conf.color}>{conf.text}</Tag>;
  };

  const getPriorityTag = (priority) => {
    const map = {
      low: { color: 'default', text: 'ต่ำ' },
      medium: { color: 'gold', text: 'ปานกลาง' },
      high: { color: 'orange', text: 'สูง' },
      urgent: { color: 'red', text: 'เร่งด่วน' }
    };
    const conf = map[priority] || { color: 'default', text: priority };
    return <Tag color={conf.color}>{conf.text}</Tag>;
  };

  const columns = [
    {
      title: 'ชื่องาน',
      dataIndex: 'task_title',
      key: 'task_title',
      render: (text, record) => (
        <a onClick={() => onTaskSelect && onTaskSelect(record)}>{text}</a>
      )
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status)
    },
    {
      title: 'ลำดับความสำคัญ',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => getPriorityTag(priority)
    },
    {
      title: 'ความคืบหน้า',
      dataIndex: 'progress_percentage',
      key: 'progress_percentage',
      render: (val) => `${val ?? 0}%`
    },
    {
      title: 'ผู้รับผิดชอบ',
      dataIndex: 'assigned_to',
      key: 'assigned_to'
    },
    {
      title: 'กำหนดส่ง',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date) =>
        date ? new Date(date).toLocaleDateString('th-TH') : '-'
    },
    {
      title: 'การจัดการ',
      key: 'actions',
      render: (text, record) => (
        <Space>
          <Select
            value={record.status}
            style={{ width: 130 }}
            onChange={(value) =>
              onStatusChange && onStatusChange(record, value)
            }
            onClick={(e) => e.stopPropagation()}
          >
            <Option value="todo">รอดำเนินการ</Option>
            <Option value="in_progress">กำลังดำเนินการ</Option>
            <Option value="testing">ทดสอบ</Option>
            <Option value="done">เสร็จสิ้น</Option>
          </Select>
          {onEdit && (
            <Button
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(record);
              }}
            >
              แก้ไข
            </Button>
          )}
          {onDelete && (
            <Button
              danger
              onClick={(e) => {
                e.stopPropagation();
                onDelete(record);
              }}
            >
              ลบ
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <Card
      title="รายการงาน"
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchTasks}
          type="primary"
        >
          รีเฟรช
        </Button>
      }
    >
      {/* ฟิลเตอร์ */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8} md={6}>
          <Select
            value={filters.status}
            onChange={(val) => handleFilterChange('status', val)}
            placeholder="สถานะ"
            style={{ width: '100%' }}
          >
            <Option value="">ทั้งหมด</Option>
            <Option value="todo">รอดำเนินการ</Option>
            <Option value="in_progress">กำลังดำเนินการ</Option>
            <Option value="testing">ทดสอบ</Option>
            <Option value="done">เสร็จสิ้น</Option>
          </Select>
        </Col>

        <Col xs={24} sm={8} md={6}>
          <Select
            value={filters.priority}
            onChange={(val) => handleFilterChange('priority', val)}
            placeholder="ลำดับความสำคัญ"
            style={{ width: '100%' }}
          >
            <Option value="">ทั้งหมด</Option>
            <Option value="low">ต่ำ</Option>
            <Option value="medium">ปานกลาง</Option>
            <Option value="high">สูง</Option>
            <Option value="urgent">เร่งด่วน</Option>
          </Select>
        </Col>

        <Col xs={24} sm={8} md={6}>
          <Input
            placeholder="ค้นหาชื่องานหรือรายละเอียด..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            onPressEnter={handleSearch}
            suffix={<SearchOutlined onClick={handleSearch} />}
          />
        </Col>

        <Col xs={24} sm={8} md={6}>
          <Space>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              ค้นหา
            </Button>
            <Button onClick={handleResetFilters}>รีเซ็ต</Button>
          </Space>
        </Col>
      </Row>

      {/* ตารางงาน */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin indicator={<SyncOutlined spin />} size="large" />
        </div>
      ) : (
        <Table
          dataSource={tasks}
          columns={columns}
          rowKey="task_id"
          pagination={false}
          onRow={(record) => ({
            onClick: () => onTaskSelect && onTaskSelect(record)
          })}
        />
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pagination.limit}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </Card>
  );
};

export default TaskList;
